import _ from "lodash";
import queryString from "query-string";
import { useCallback, useMemo } from "react";
import { useQuery } from "react-query";

import AxiosInstance from "./AxiosInstance";
import { cleanJsonApiData, defaultQueryOptions } from "./utils";

/**
 * Wrapper around `useQuery`
 * https://tanstack.com/query/v4/docs/reference/useQuery?from=reactQueryV3&original=https://react-query-v3.tanstack.com/reference/useQuery
 *
 * @param {string} url
 * @param {object} queryParams
 * @param {object} options
 * @returns {Array} see useQuery documentation for return values. Also adds `cleanedData` to the returnInfo.
 */
const useFetch = (url, queryParams = {}, options = {}) => {
  // Some API_ENDPOINTS entries pre-embed `?company=…` into the URL, so the
  // separator must adapt instead of always being `?`.
  const separator = url.includes("?") ? "&" : "?";
  const qs = _.isEmpty(queryParams) ? "" : `${separator}${queryString.stringify(queryParams)}`;
  const apiUrl = `${url}${qs}`;

  const queryFn = useCallback(
    () =>
      AxiosInstance.get(apiUrl)
        .then(response => response.data)
        // Preserve the original error (typically an AxiosError) when there is
        // no `response` (network error, CORS failure, request cancellation).
        // Rejecting with `undefined` here turns into a non-Error rejection that
        // surfaces as the cryptic "Uncaught (in promise) <minified-token>"
        // console error (ZX-15035).
        .catch(error => Promise.reject(error?.response ?? error)),
    [apiUrl]
  );

  const queryOptions = { ...options, ...defaultQueryOptions };

  const queryInfo = useQuery([url, queryParams], queryFn, queryOptions);

  const cleanedData = useMemo(() => {
    // JSON API data
    if (queryInfo.data?.data) {
      return cleanJsonApiData(queryInfo.data);
    }
    // HD API data
    return queryInfo.data;
  }, [queryInfo.data]);

  return useMemo(
    () => ({
      ...queryInfo,
      cleanedData,
    }),
    [queryInfo, cleanedData]
  );
};

export default useFetch;
